import {
  accountMutationHeaders,
  browserAccountStateSessionRepository,
  readBoundedAccountJson,
} from "../account/account-state-session-repository.js";

const UINT32_MAX = 4_294_967_295;
const integer = (value, minimum = 0) => Number.isSafeInteger(value) && value >= minimum && value <= UINT32_MAX;
function exact(value, fields) {
  if (!value || typeof value !== "object" || Array.isArray(value)
    || Object.keys(value).length !== fields.length || fields.some((key) => !Object.hasOwn(value, key))) {
    throw new TypeError("Unsupported fishing title contract");
  }
}
function text(value, limit) {
  if (typeof value !== "string" || value.length > limit) throw new TypeError("Invalid fishing title text");
}

export function decodeFishingTitles(value) {
  exact(value, ["schema", "revision", "titles"]);
  if (value.schema !== "fishystuff.fishing-titles.v1" || !Array.isArray(value.titles) || value.titles.length > 4096) {
    throw new TypeError("Unsupported fishing title catalog");
  }
  text(value.revision, 256);
  if (!value.revision) throw new TypeError("Missing fishing title revision");
  const ids = new Set();
  for (const title of value.titles) {
    exact(title, ["titleId", "itemId", "kind", "ordinal", "color", "availability", "name", "description", "requirement", "reports"]);
    if (!integer(title.titleId, 1) || ids.has(title.titleId) || (title.itemId !== null && !integer(title.itemId, 1))
      || !["catch", "contest", "quest", "exchange"].includes(title.kind) || !integer(title.ordinal)) {
      throw new TypeError("Invalid fishing title identity");
    }
    ids.add(title.titleId);
    exact(title.color, ["argb", "source"]);
    if (!integer(title.color.argb) || !["native_default", "title_override"].includes(title.color.source)) {
      throw new TypeError("Invalid original title color");
    }
    Object.freeze(title.color);
    if (title.availability !== null) {
      exact(title.availability, ["obtainable", "evidence", "source"]);
      if (typeof title.availability.obtainable !== "boolean"
        || title.availability.evidence !== "community_reference") throw new TypeError("Invalid title availability");
      text(title.availability.source, 16384);
      Object.freeze(title.availability);
    }
    if (title.name !== null) text(title.name, 4096);
    if (title.description !== null) text(title.description, 32768);
    if (title.requirement !== null) {
      exact(title.requirement, ["minimum", "maximum", "evidence", "source"]);
      const requirement = title.requirement;
      if (!integer(requirement.minimum) || !integer(requirement.maximum) || requirement.minimum > requirement.maximum
        || requirement.evidence !== "community_reference") throw new TypeError("Invalid title requirement");
      text(requirement.source, 16384);
      Object.freeze(requirement);
    }
    exact(title.reports, ["count", "average"]);
    if (!integer(title.reports.count) || (title.reports.count === 0 ? title.reports.average !== null
      : !Number.isFinite(title.reports.average) || title.reports.average < 0 || title.reports.average > UINT32_MAX)) {
      throw new TypeError("Invalid fishing title report aggregate");
    }
    Object.freeze(title.reports);
    Object.freeze(title);
  }
  Object.freeze(value.titles);
  return Object.freeze(value);
}

export function createFishingTitlesApi({ resolveApiUrl, fetchImpl = globalThis.fetch }) {
  return Object.freeze({
    async catalog({ lang, revision = "", signal }) {
      const query = new URLSearchParams({ lang });
      if (revision.startsWith("dolt:")) query.set("ref", revision.slice(5));
      const response = await fetchImpl(resolveApiUrl(`/api/v1/fish/titles?${query}`), {
        headers: { Accept: "application/json" }, signal,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return decodeFishingTitles(await readBoundedAccountJson(response, "Fishing titles", 4 * 1024 * 1024));
    },
    async report({ titleId, catchCount, obtainedAt }, { signal } = {}) {
      const session = await browserAccountStateSessionRepository().load();
      if (session.kind !== "ready") throw new Error("signedOut");
      const response = await fetchImpl(resolveApiUrl("/api/v1/account/fishing-title-reports"), {
        method: "POST", credentials: "include", cache: "no-store", redirect: "error", signal,
        headers: accountMutationHeaders(session),
        body: JSON.stringify({ schema: "fishystuff.title-acquisition-report.v1", titleId, catchCount, obtainedAt }),
      });
      if (response.status === 401) throw new Error("signedOut");
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const acknowledgement = await readBoundedAccountJson(response, "Title acquisition report", 4096);
      exact(acknowledgement, ["titleId", "catchCount", "obtainedAt"]);
      if (acknowledgement.titleId !== titleId || acknowledgement.catchCount !== catchCount
        || acknowledgement.obtainedAt !== obtainedAt) throw new TypeError("Mismatched title report acknowledgement");
      return acknowledgement;
    },
  });
}
